import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Plus, Search, Trash2, Loader2 } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton } from "@/components/Card";
import { useEstandares } from "@/lib/api/estandares";
import type { EstandarListItem } from "@/lib/api/estandares";
import type { Standard } from "@/data";

type EstadoFilter = "todos" | "borrador" | "en-revision" | "publicado";
type SortKey = "codigo" | "titulo" | "candidatosActivos" | "certificadosEmitidos" | "ultimaActualizacion";

const PAGE_SIZE = 10;

// Adapter: EstandarListItem (backend) → Standard (UI mockup)
function toUiStandard(e: EstandarListItem): Standard {
  return {
    codigo: e.codigo,
    titulo: e.nombre,
    sector: e.sector ?? "—",
    comite: undefined,
    vigencia: e.vigencia_meses ? `${Math.round(e.vigencia_meses / 12)} años` : "—",
    estado:
      e.estado === "publicado" ? "publicado" :
      e.estado === "revision"  ? "en-revision" :
      "borrador",
    elementos: e.elementos_count ?? 0,
    criterios: e.criterios_count ?? 0,
    candidatosActivos: e.candidatos_activos ?? 0,
    certificadosEmitidos: e.certificados_emitidos ?? 0,
    ultimaActualizacion: (e.updated_at ?? e.created_at ?? "").slice(0, 10),
    tipo: e.tipo,
    version: e.version,
    descripcion: e.descripcion ?? undefined,
    proposito: e.proposito ?? undefined,
  };
}

export default function StandardsList() {
  const { data: backendList = [], isLoading, error } = useEstandares({});

  const all = useMemo(
    () => backendList.map(toUiStandard),
    [backendList],
  );

  function handleDelete(s: Standard) {
    if (!window.confirm(`¿Eliminar el estándar ${s.codigo} del catálogo?`)) return;
    // TODO conectar a useDeleteEstandar (DELETE /estandares/:id)
    alert("Eliminación pendiente de cablear");
  }

  const [query, setQuery] = useState("");
  const [estado, setEstado] = useState<EstadoFilter>("todos");
  const [sector, setSector] = useState<string>("todos");
  const [sortKey, setSortKey] = useState<SortKey>("codigo");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(1);

  const sectores = useMemo(
    () => Array.from(new Set(all.map((s) => s.sector))).sort((a, b) => a.localeCompare(b, "es")),
    [all],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = all
      .filter((s) => (estado === "todos" ? true : s.estado === estado))
      .filter((s) => (sector === "todos" ? true : s.sector === sector))
      .filter((s) =>
        !q
          ? true
          : s.codigo.toLowerCase().includes(q) ||
            s.titulo.toLowerCase().includes(q) ||
            s.sector.toLowerCase().includes(q),
      );
    const dir = sortDir === "asc" ? 1 : -1;
    list.sort((a, b) => {
      const va = a[sortKey] as string | number;
      const vb = b[sortKey] as string | number;
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "es") * dir;
    });
    return list;
  }, [all, query, estado, sector, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function toggleSort(k: SortKey) {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  }

  const headers: { k: SortKey | null; label: string }[] = [
    { k: "codigo", label: "Código" },
    { k: "titulo", label: "Estándar" },
    { k: null, label: "Sector" },
    { k: null, label: "Estado" },
    { k: "candidatosActivos", label: "Candidatos" },
    { k: "certificadosEmitidos", label: "Certificados" },
    { k: null, label: "Acciones" },
  ];

  return (
    <AppLayout breadcrumb={["Estándares"]}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>Catálogo de estándares de competencia</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>{all.length} estándares activos</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link href="/admin/estandares/nuevo" data-testid="link-create-standard">
            <PrimaryButton><Plus size={14} /> Nuevo estándar</PrimaryButton>
          </Link>
        </div>
      </div>

      <Card padding={0}>
        <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--cc-gray-200)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 240 }}>
            <Search size={15} color="var(--cc-gray-400)" />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Buscar por código, título o sector..."
              data-testid="input-search-standards"
              style={{ border: "none", outline: "none", fontSize: 13, flex: 1, background: "transparent" }}
            />
          </div>
          <Select
            value={estado}
            onChange={(v) => { setEstado(v as EstadoFilter); setPage(1); }}
            testId="select-estado"
            options={[
              { value: "todos", label: "Todos los estados" },
              { value: "borrador", label: "Borrador" },
              { value: "en-revision", label: "En revisión" },
              { value: "publicado", label: "Publicado" },
            ]}
          />
          <Select
            value={sector}
            onChange={(v) => { setSector(v); setPage(1); }}
            testId="select-sector"
            options={[{ value: "todos", label: "Todos los sectores" }, ...sectores.map((s) => ({ value: s, label: s }))]}
          />
        </div>

        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
              {headers.map((h) => (
                <th key={h.label || "actions"} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase", letterSpacing: "0.04em", cursor: h.k ? "pointer" : "default" }} onClick={() => h.k && toggleSort(h.k)}>
                  {h.label}{h.k === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slice.length === 0 && (
              <tr><td colSpan={headers.length} style={{ padding: 24, textAlign: "center", color: "var(--cc-gray-500)" }}>Sin resultados con los filtros actuales.</td></tr>
            )}
            {slice.map((s) => (
              <tr key={s.codigo} style={{ borderTop: "1px solid var(--cc-gray-100)" }} data-testid={`row-standard-${s.codigo}`}>
                <td style={{ padding: "12px 14px", fontWeight: 700, color: "var(--cc-navy)" }}>{s.codigo}</td>
                <td style={{ padding: "12px 14px", color: "var(--cc-gray-800)", maxWidth: 360 }}>{s.titulo}</td>
                <td style={{ padding: "12px 14px", color: "var(--cc-gray-600)" }}>{s.sector}</td>
                <td style={{ padding: "12px 14px" }}>
                  <Badge tone={s.estado === "publicado" ? "green" : s.estado === "en-revision" ? "amber" : "gray"}>
                    {s.estado === "publicado" ? "Publicado" : s.estado === "en-revision" ? "En revisión" : "Borrador"}
                  </Badge>
                </td>
                <td style={{ padding: "12px 14px", color: "var(--cc-gray-700)" }}>{s.candidatosActivos}</td>
                <td style={{ padding: "12px 14px", color: "var(--cc-gray-700)" }}>{s.certificadosEmitidos.toLocaleString("es-MX")}</td>
                <td style={{ padding: "12px 14px", verticalAlign: "middle" }}>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
                    <Link href={`/admin/estandares/${s.codigo}`} data-testid={`link-open-${s.codigo}`}>
                      <PrimaryButton size="sm" variant="outline">Abrir</PrimaryButton>
                    </Link>
                    <button
                      onClick={() => handleDelete(s)}
                      data-testid={`button-delete-${s.codigo}`}
                      title="Eliminar estándar"
                      style={{
                        width: 30, height: 30, borderRadius: 6,
                        border: "1px solid var(--cc-gray-200)",
                        background: "white", color: "var(--cc-gray-500)",
                        cursor: "pointer",
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--cc-gray-100)", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "var(--cc-gray-600)" }}>
          <div>Mostrando {slice.length} de {filtered.length}</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="button-page-prev" style={pagerBtn(safePage <= 1)}>Anterior</button>
            <span>Página {safePage} de {totalPages}</span>
            <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="button-page-next" style={pagerBtn(safePage >= totalPages)}>Siguiente</button>
          </div>
        </div>
      </Card>
    </AppLayout>
  );
}

function pagerBtn(disabled: boolean): React.CSSProperties {
  return {
    padding: "4px 10px",
    border: "1px solid var(--cc-gray-200)",
    borderRadius: 6,
    background: "white",
    color: disabled ? "var(--cc-gray-400)" : "var(--cc-navy)",
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 12,
  };
}

function Select({ value, onChange, options, testId }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; testId?: string }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      style={{ padding: "6px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 12, background: "white", color: "var(--cc-gray-800)" }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}
